import React from "react";

import type { AppArgSpec } from "@/shared/types";

/* Renders `code` spans for backtick-wrapped fragments in descriptions — the
   catalog's prose is written that way and a raw backtick reads as a typo. */
function InlineCode({ text }: { text: string }) {
  const parts = text.split("`");
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <code key={i} className="text-[11.5px] px-1 py-[1px] rounded-[4px] bg-[var(--surface-2)] text-[var(--text-secondary)]">
            {p}
          </code>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

/* One argument of a tool, one config field of a trigger, one field of an event
   payload — same shape on the wire (CONTRACT §6 `arg`), so one table.
   `req`, `def` and `values` are what a caller has to know before writing a
   value, so none of them is left to the description to mention. */
export function ArgsTable({ args, label = "Parameters" }: { args: AppArgSpec[]; label?: string }) {
  if (!args.length) {
    return (
      <div className="px-4 py-3 text-[12.5px] text-[var(--text-quaternary)] italic">
        No configuration required.
      </div>
    );
  }
  return (
    <div className="px-4 pb-3.5 pt-1">
      <div className="section-label pb-1.5">{label}</div>
      <div className="rounded-[10px] ring-1 ring-[var(--hairline)] overflow-hidden">
        {args.map((a, i) => (
          /* Three columns from sm; on a phone the description goes under the name and type */
          <div
            key={a.name}
            className={`flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 px-3 py-2 ${i > 0 ? "border-t hairline" : ""}`}
          >
            <div className="flex items-start gap-3 min-w-0 sm:shrink-0">
              <div className="sm:w-[150px] min-w-0 flex items-center gap-1.5">
                <code className="text-[12.5px] text-[var(--text-primary)] truncate">{a.name}</code>
                {a.req && (
                  <span className="text-[var(--danger)] text-[12px] leading-none" title="Required">*</span>
                )}
              </div>
              <span className="shrink-0 mt-[1px] whitespace-nowrap font-mono text-[10.5px] font-medium tracking-[0.02em] px-[6px] py-[2px] rounded-[5px] bg-[var(--surface-2)] text-[var(--text-secondary)]">
                {a.type}
              </span>
            </div>
            <div className="flex-1 min-w-0 break-words text-[12.5px] leading-[1.55] text-[var(--text-secondary)]">
              <InlineCode text={a.desc} />
              {(a.values?.length || a.def !== undefined) && (
                <div className="text-[var(--text-tertiary)]">
                  {a.values?.length ? <InlineCode text={`One of ${a.values.map((v) => `\`${v}\``).join(" · ")}. `} /> : null}
                  {a.def !== undefined ? <InlineCode text={`Default \`${String(a.def)}\`.`} /> : null}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
